import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon } from 'lucide-react';
import { members } from '../data/members';
import { useTheme } from '../context/ThemeContext';
import { useSEO } from '../hooks/useSEO';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.6
  }
};
export function MemberProfile() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    theme
  } = useTheme();
  const member = members.find(m => m.slug === slug);
  useSEO({
    title: member ? `${member.name} (${member.role}) – Tuesday Liveband Kempten` : 'Bandmitglied nicht gefunden – Tuesday',
    description: member ? `${member.name} spielt ${member.role} bei Tuesday, der Liveband aus Kempten im Allgäu. ${member.bio}` : 'Dieses Bandmitglied wurde nicht gefunden.',
    path: member ? `/mitglieder/${member.slug}` : undefined,
    image: member ? theme === 'dark' ? (member as any).imageDark : (member as any).imageLight : undefined
  });
  if (!member) {
    return <div className="w-full min-h-screen flex items-center justify-center px-4" data-id="element-220">
        <div className="text-center" data-id="element-221">
          <h1 className="font-display text-4xl font-bold text-primary mb-4" data-id="element-222">
            Mitglied nicht gefunden
          </h1>
          <Link to="/#mitglieder" className="text-accent hover:underline" data-id="element-223">
            ← Zurück zur Band
          </Link>
        </div>
      </div>;
  }
  const memberImage = theme === 'dark' ? (member as any).imageDark : (member as any).imageLight;
  return <div className="w-full min-h-screen py-16 px-4 sm:px-6 lg:px-8" data-id="element-224">
      <div className="max-w-4xl mx-auto" data-id="element-225">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} data-id="element-226">
          <Link to="/#mitglieder" className="inline-flex items-center gap-2 text-muted hover:text-accent transition-colors mb-8" data-id="element-227">
            <ArrowLeftIcon size={20} data-id="element-228" />
            Zurück zur Band
          </Link>
        </motion.div>

        {/* Hero Section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} className="mb-12" data-id="element-229">
          <div className="aspect-square sm:aspect-[4/3] bg-surface rounded-lg overflow-hidden flex items-center justify-center mb-8 border border-border-base" data-id="element-230">
            {memberImage ? <img src={memberImage} alt={`${member.name} — ${member.role}`} className="w-full h-full object-cover object-top" data-id="element-231" /> : <span className="font-display text-9xl text-primary opacity-20" data-id="element-232">
                {member.imagePlaceholder}
              </span>}
          </div>

          <h1 className="font-display text-5xl sm:text-6xl font-bold text-primary mb-2" data-id="element-233">
            {member.name}
          </h1>
          <p className="text-2xl text-accent mb-6" data-id="element-234">{member.role}</p>
        </motion.div>

        {/* Bio Section */}
        <motion.div {...fadeInUp} className="mb-12" data-id="element-235">
          <h2 className="font-display text-3xl font-bold text-primary mb-4" data-id="element-236">
            Über {member.name}
          </h2>
          <p className="text-lg text-muted leading-relaxed mb-6" data-id="element-237">
            {member.bio}
          </p>

          <div className="bg-[var(--color-secondary-hover)] border-l-4 border-secondary p-6 rounded" data-id="element-238">
            <p className="text-primary italic text-lg" data-id="element-239">"{member.quote}"</p>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div {...fadeInUp} className="bg-surface border border-border-base rounded-lg p-8 text-center" data-id="element-240">
          <h3 className="font-display text-2xl font-bold text-primary mb-4" data-id="element-241">
            Tuesday live erleben
          </h3>
          <p className="text-muted mb-6" data-id="element-242">
            Buche Tuesday für euer Event und erlebe {member.name} und die Band
            live auf der Bühne.
          </p>
          <Link to="/buchen" className="inline-block px-8 py-3 bg-accent text-[var(--color-text-inv)] font-medium rounded hover:bg-[var(--color-accent-solid-hover)] transition-colors" data-id="element-243">
            Tuesday buchen
          </Link>
        </motion.div>
      </div>
    </div>;
}